"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { Mic, MicOff, Send } from "lucide-react";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
export type WordTimestamp = {
  word: string;
  start?: number;
  end?: number;
};
{/* ============================================================
    MA2TIC ORG — Proprietary Software
    © 2026 MA2TIC. All Rights Reserved.
     Licensed to: MA2TIC Organisation
    Owners: Archana Thakur | Tanisha Bhardwaj |
            Manika Kutiyal | Aditya Verma
     NOTICE: This software is proprietary and confidential.
    Unauthorized copying, fragmentation, redistribution,
    or publication of this code, in whole or in part,
    is strictly prohibited without prior written permission
    from the MA2TIC development team.
     For permissions and licensing inquiries, contact MA2TIC.
    ============================================================ */}
export type AnalysisInput = {
  type: "text";
  content: string;
} | {
  type: "transcript";
  content: string;
  pauseMap?: number[];
  wordTimestamps?: WordTimestamp[];
  duration?: number;
} | {
  type: "file";
  file: File;
};
type AgentStep = {
  name: string;
  status: "pending" | "running" | "done" | "error";
  detail?: string;
};
type InputCommandPanelProps = {
  onSubmit?: (input: AnalysisInput) => void;
  isLoading?: boolean;
  agentSteps?: AgentStep[];
  placeholder?: string;
};
const GLASS: React.CSSProperties = {
  background: "var(--nt-glass)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid var(--nt-glass-border)",
  boxShadow: "var(--nt-glass-shadow)"
};
function WaveBars(t0) {
  const $ = _c(3);
  const {
    level
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Array.from({
      length: 20
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== level) {
    t2 = <div className="flex items-center gap-[2px] h-5">{t1.map((_, i) => {
        const phase = i / 20 * Math.PI * 2;
        const natural = (Math.sin(phase) + 1) / 2;
        const height = 8 + (natural * 0.4 + level * 0.6) * 28;
        return <div key={i} className="rounded-full transition-all duration-75" style={{
          width: 2,
          height,
          background: level > 0.05 ? `rgba(239,159,39,${0.5 + level * 0.5})` : "rgba(255,255,255,0.15)"
        }} />;
      })}</div>;
    $[1] = level;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function InputCommandPanel(t0) {
  const $ = _c(93);
  const {
    onSubmit,
    isLoading: t1,
    agentSteps: t2,
    placeholder: t3
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const agentSteps = t4;
  const placeholder = t3 === undefined ? "Ask about cognitive signature analysis\u2026" : t3;
  const [text, setText] = useState("");
  let t5;
  if ($[2] !== onSubmit) {
    t5 = t6 => {
      const {
        transcript,
        pauseMap,
        wordTimestamps,
        duration
      } = t6;
      setText("");
      onSubmit?.({
        type: "transcript",
        content: transcript,
        pauseMap,
        wordTimestamps,
        duration
      });
    };
    $[2] = onSubmit;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleTranscriptReady = t5;
  const {
    isRecording,
    isTranscribing,
    recordSeconds,
    audioLevel,
    liveTranscript,
    silenceCountdown,
    errorMessage,
    toggle
  } = useVoiceRecorder(handleTranscriptReady);
  let t6;
  let t7;
  if ($[4] !== isRecording || $[5] !== isTranscribing) {
    t6 = () => {
      if (!isRecording && !isTranscribing) {}
    };
    t7 = [isRecording, isTranscribing];
    $[4] = isRecording;
    $[5] = isTranscribing;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== isLoading || $[9] !== onSubmit || $[10] !== text) {
    t8 = () => {
      if (!text.trim() || isLoading) {
        return;
      }
      onSubmit?.({
        type: "text",
        content: text.trim()
      });
      setText("");
    };
    $[8] = isLoading;
    $[9] = onSubmit;
    $[10] = text;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleSend = t8;
  let t9;
  if ($[12] !== handleSend) {
    t9 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
    $[12] = handleSend;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleKeyDown = t9;
  const statusDot = _temp;
  const statusLabel = _temp2;
  const showLive = isRecording;
  const showTranscribing = isTranscribing;
  let t10;
  if ($[14] !== agentSteps || $[15] !== isLoading) {
    t10 = agentSteps.length > 0 && <div className="rounded-xl overflow-hidden" style={GLASS}><div className="px-4 py-2.5 flex flex-col gap-1">{agentSteps.map((step, i) => <div key={i} className="flex items-center gap-2.5"><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(step.status)}`} style={step.status === "pending" ? {
            background: "var(--nt-track)"
          } : {}} /><span className={`text-xs ${statusLabel(step.status)}`} style={step.status === "pending" ? {
            color: "var(--nt-text-xs)"
          } : {}}>{step.name}</span>{step.detail && <span className="text-xs ml-auto" style={{
            color: "var(--nt-text-ghost)"
          }}>{step.detail}</span>}</div>)}{isLoading && <div className="flex items-center gap-2.5 mt-0.5 pt-1.5" style={{
          borderTop: "1px solid var(--nt-divider)"
        }}><span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{
            background: "var(--nt-track)"
          }} /><span className="text-xs" style={{
            color: "var(--nt-text-xs)"
          }}>Processing…</span></div>}</div></div>;
    $[14] = agentSteps;
    $[15] = isLoading;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== audioLevel || $[18] !== liveTranscript || $[19] !== recordSeconds || $[20] !== showLive || $[21] !== silenceCountdown) {
    t11 = showLive && <div className="rounded-xl px-4 py-3 flex flex-col gap-2.5" style={{
      ...GLASS,
      border: "1px solid rgba(239,159,39,0.35)",
      boxShadow: "0 0 18px rgba(239,159,39,0.08)"
    }}><div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full animate-pulse" style={{
            background: "#ef9f27"
          }} /><span className="text-[10px] font-semibold tracking-widest uppercase" style={{
            color: "#ef9f27",
            fontFamily: "var(--font-jetbrains-mono)"
          }}>Live · {`${Math.floor(recordSeconds / 60).toString().padStart(2, "0")}:${(recordSeconds % 60).toString().padStart(2, "0")}`}</span></div><div className="flex items-center gap-3 flex-wrap sm:justify-end">{silenceCountdown !== null && <span className="text-[10px] tabular-nums font-medium px-2 py-0.5 rounded-full" style={{
            background: "rgba(239,159,39,0.12)",
            color: "#ef9f27",
            fontFamily: "var(--font-jetbrains-mono)",
            border: "1px solid rgba(239,159,39,0.25)"
          }}>auto-submit in {silenceCountdown}s</span>}<WaveBars level={audioLevel} /></div></div><div className="text-[15px] leading-relaxed break-words min-h-[1.4em]" style={{
        color: liveTranscript ? "var(--nt-text-hi)" : "rgba(255,255,255,0.22)",
        fontFamily: "var(--font-dm-sans)",
        letterSpacing: "0.01em"
      }}>{liveTranscript || <span className="italic" style={{
          color: "rgba(255,255,255,0.22)",
          fontSize: 13
        }}>Listening…</span>}<span className="inline-block w-[2px] h-[1em] ml-0.5 align-middle animate-pulse rounded-sm" style={{
          background: "#ef9f27",
          verticalAlign: "text-bottom"
        }} /></div></div>;
    $[17] = audioLevel;
    $[18] = liveTranscript;
    $[19] = recordSeconds;
    $[20] = showLive;
    $[21] = silenceCountdown;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== showTranscribing) {
    t12 = showTranscribing && <div className="rounded-xl px-4 py-3 flex items-center gap-3" style={{
      ...GLASS,
      border: "1px solid rgba(99,179,237,0.25)"
    }}><div className="w-3.5 h-3.5 rounded-full border-2 border-blue-400/30 border-t-blue-400 animate-spin shrink-0" /><span className="text-[12px] font-medium" style={{
        color: "rgba(99,179,237,0.85)",
        fontFamily: "var(--font-dm-sans)"
      }}>Transcribing with Gemini — analysing shortly…</span></div>;
    $[23] = showTranscribing;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== errorMessage || $[26] !== showLive || $[27] !== showTranscribing) {
    t13 = errorMessage && !showLive && !showTranscribing && <div className="rounded-xl px-4 py-2.5 text-[12px]" style={{
      ...GLASS,
      border: "1px solid rgba(248,113,113,0.35)",
      color: "rgba(254,202,202,0.95)"
    }}>{errorMessage}</div>;
    $[25] = errorMessage;
    $[26] = showLive;
    $[27] = showTranscribing;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = isRecording ? liveTranscript : text;
  let t15;
  if ($[29] !== isRecording) {
    t15 = e_0 => {
      if (!isRecording) {
        setText(e_0.target.value);
      }
    };
    $[29] = isRecording;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = isRecording ? "" : placeholder;
  const t17 = isLoading || isRecording;
  const t18 = isRecording ? "#ef9f27" : "var(--nt-text-hi)";
  const t19 = isRecording ? 0.85 : 1;
  let t20;
  if ($[31] !== t18 || $[32] !== t19) {
    t20 = {
      color: t18,
      opacity: t19
    };
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== handleKeyDown || $[35] !== isRecording || $[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t17 || $[40] !== t20) {
    t21 = <textarea value={t14} onChange={t15} onKeyDown={handleKeyDown} placeholder={t16} rows={2} disabled={t17} readOnly={isRecording} className="w-full px-4 pt-3 pb-1 text-sm bg-transparent resize-none outline-none" style={t20} />;
    $[34] = handleKeyDown;
    $[35] = isRecording;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <style>{"\n          textarea { caret-color: var(--nt-text-hi); }\n          textarea::placeholder { color: var(--nt-text-ghost) !important; }\n          textarea:read-only { cursor: default; }\n        "}</style>;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = isTranscribing ? "1px solid rgba(99,179,237,0.45)" : isRecording ? "1px solid rgba(239,159,39,0.65)" : "1px solid rgba(43,208,189,0.55)";
  const t24 = isTranscribing ? "0 0 10px rgba(99,179,237,0.25)" : isRecording ? "0 0 12px rgba(239,159,39,0.35)" : "0 0 10px rgba(43,208,189,0.25)";
  let t25;
  if ($[43] !== t23 || $[44] !== t24) {
    t25 = <span aria-hidden={true} className="absolute -inset-1.5 rounded-full animate-pulse pointer-events-none" style={{
      border: t23,
      boxShadow: t24
    }} />;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = isRecording ? "Stop recording" : isTranscribing ? "Transcribing\u2026" : "Record speech";
  const t27 = isTranscribing ? "rgba(99,179,237,0.85)" : isRecording ? "#ef9f27" : "var(--nt-icon)";
  const t28 = isRecording ? "rgba(239,159,39,0.12)" : isTranscribing ? "rgba(99,179,237,0.08)" : undefined;
  const t29 = isRecording ? "1px solid rgba(239,159,39,0.35)" : isTranscribing ? "1px solid rgba(99,179,237,0.25)" : "none";
  const t30 = isRecording ? "0 0 10px rgba(239,159,39,0.2)" : undefined;
  let t31;
  if ($[46] !== t27 || $[47] !== t28 || $[48] !== t29 || $[49] !== t30) {
    t31 = {
      color: t27,
      background: t28,
      border: t29,
      boxShadow: t30
    };
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== isRecording || $[52] !== isTranscribing) {
    t32 = isTranscribing ? <div className="w-3 h-3 rounded-full border border-blue-400/40 border-t-blue-400 animate-spin" /> : isRecording ? <MicOff size={13} /> : <Mic size={13} />;
    $[51] = isRecording;
    $[52] = isTranscribing;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== isTranscribing || $[55] !== t26 || $[56] !== t31 || $[57] !== t32 || $[58] !== toggle) {
    t33 = <button onClick={toggle} disabled={isTranscribing} title={t26} className="relative z-[1] w-7 h-7 rounded-full flex items-center justify-center transition-all" style={t31}>{t32}</button>;
    $[54] = isTranscribing;
    $[55] = t26;
    $[56] = t31;
    $[57] = t32;
    $[58] = toggle;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t25 || $[61] !== t33) {
    t34 = <div className="relative flex items-center justify-center">{t25}{t33}</div>;
    $[60] = t25;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span className="text-[10px] leading-snug min-w-[180px] flex-1" style={{
      color: "var(--nt-text-xs)",
      fontFamily: "var(--font-dm-sans)"
    }}>Record your audio: click mic. Auto-submit after ~3s of silence.</span>;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== isRecording || $[65] !== isTranscribing || $[66] !== recordSeconds) {
    t36 = (isRecording || isTranscribing) && <span className="text-[11px] font-medium tabular-nums" style={{
      color: isTranscribing ? "rgba(99,179,237,0.75)" : "#ef9f27",
      fontFamily: "var(--font-jetbrains-mono)"
    }}>{isRecording ? `${Math.floor(recordSeconds / 60).toString().padStart(2, "0")}:${(recordSeconds % 60).toString().padStart(2, "0")}` : "transcribing\u2026"}</span>;
    $[64] = isRecording;
    $[65] = isTranscribing;
    $[66] = recordSeconds;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="hidden sm:block flex-1" />;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== isLoading || $[70] !== isRecording || $[71] !== text) {
    t38 = !text.trim() || isLoading || isRecording;
    $[69] = isLoading;
    $[70] = isRecording;
    $[71] = text;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      background: "var(--nt-btn-bg)",
      color: "var(--nt-btn-fg)"
    };
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== isLoading) {
    t40 = isLoading ? <div className="w-3 h-3 rounded-full border-2 border-current/40 border-t-current animate-spin" /> : <><Send size={11} /><span>Analyse</span></>;
    $[74] = isLoading;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== handleSend || $[77] !== t38 || $[78] !== t40) {
    t41 = <button onClick={handleSend} disabled={t38} className="h-7 px-3 rounded-full flex items-center gap-1.5 text-[11px] font-medium transition-colors disabled:opacity-25 ml-auto sm:ml-0" style={t39}>{t40}</button>;
    $[76] = handleSend;
    $[77] = t38;
    $[78] = t40;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] !== t34 || $[81] !== t36 || $[82] !== t41) {
    t42 = <div className="flex flex-wrap items-center gap-2 px-3 pb-2.5 pt-1">{t34}{t35}{t36}{t37}{t41}</div>;
    $[80] = t34;
    $[81] = t36;
    $[82] = t41;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== t21 || $[85] !== t42) {
    t43 = <div className="rounded-xl overflow-hidden" style={GLASS}>{t21}{t22}{t42}</div>;
    $[84] = t21;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t10 || $[88] !== t11 || $[89] !== t12 || $[90] !== t13 || $[91] !== t43) {
    t44 = <div className="w-full max-w-[42rem] mx-auto flex flex-col gap-2">{t10}{t11}{t12}{t13}{t43}</div>;
    $[87] = t10;
    $[88] = t11;
    $[89] = t12;
    $[90] = t13;
    $[91] = t43;
    $[92] = t44;
  } else {
    t44 = $[92];
  }
  return t44;
}
function _temp2(s_0) {
  if (s_0 === "running") {
    return "text-amber-500 font-medium";
  }
  if (s_0 === "done") {
    return "text-emerald-600";
  }
  if (s_0 === "error") {
    return "text-red-500";
  }
  return "";
}
function _temp(s) {
  if (s === "running") {
    return "bg-amber-400 animate-pulse";
  }
  if (s === "done") {
    return "bg-emerald-500";
  }
  if (s === "error") {
    return "bg-red-500";
  }
  return "";
}